import { useCallback, useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  PlayWalletStatus,
  choosePlayWallet,
  getPlayWalletStatus,
  selectedWalletAddressVar
} from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'

/**
 * The user's linked wallets, the one selected for play and how the wallet
 * connected in the browser relates to them. `status` is null while there is
 * no signed-in user or no connected wallet.
 */
export const usePlayWallet = () => {
  const user = useReactiveVar(userVar)
  const selectedAddress = useReactiveVar(selectedWalletAddressVar)
  const { publicKey } = useWallet()
  const connectedAddress = publicKey?.toBase58() ?? null

  const wallets = useMemo(() => user?.wallets ?? [], [user?.wallets])
  const linkedAddresses = useMemo(
    () => wallets.map(wallet => wallet.address),
    [wallets]
  )

  const status: PlayWalletStatus | null =
    user && connectedAddress ?
      getPlayWalletStatus(connectedAddress, linkedAddresses, selectedAddress)
    : null

  const selectWallet = useCallback(
    (address: string) => {
      if (!user || !linkedAddresses.includes(address)) return

      choosePlayWallet(user.id, address)
    },
    [user, linkedAddresses]
  )

  return {
    wallets,
    linkedAddresses,
    selectedAddress,
    connectedAddress,
    status,
    selectWallet
  }
}
